// Runtime SPA update detector.
//
// Problem: after a fresh deploy, phones/tablets keep serving the previous
// index.html from browser (or upstream) cache — the new hashed JS chunks are
// on disk but the stale index still points at the OLD chunk filenames, so
// residents see the previous portal for hours/days.
//
// Fix (belt & braces):
//   1) index.html carries Cache-Control:no-cache meta tags (see public/index.html)
//   2) A <meta name="build-version"> is baked in per build.
//   3) Every deploy also ships /version.txt containing the same token.
//   4) On boot we read (2). On every router transition we fetch (3) with a
//      cache-buster; if it differs from what the tab booted with, a new
//      build is live and we prompt the user to refresh.
//
// The prompt is intentionally non-destructive (a small toast the user must
// click) so we don't yank a payment mid-STK-push.

const BOOT_META = document.querySelector('meta[name="build-version"]');
const BOOT_VERSION = BOOT_META ? (BOOT_META.getAttribute('content') || '').trim() : '';

// Skip in dev builds where the meta placeholder is left literal.
const DISABLED = !BOOT_VERSION
  || BOOT_VERSION.startsWith('<%=')
  || BOOT_VERSION === 'null';

let checking = false;
let promptShown = false;
let lastCheckedAt = 0;
const MIN_INTERVAL_MS = 30 * 1000; // don't hammer /version.txt

async function checkNow() {
  if (DISABLED || promptShown || checking) return;
  if (Date.now() - lastCheckedAt < MIN_INTERVAL_MS) return;
  checking = true;
  try {
    // cache: 'no-store' + query-string ⇒ bypass every layer.
    const res = await fetch('/version.txt?t=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return;
    const latest = (await res.text()).trim();
    if (latest && latest !== BOOT_VERSION) {
      showUpdatePrompt();
    }
  } catch (_) {
    // network hiccup — try again on the next route change.
  } finally {
    lastCheckedAt = Date.now();
    checking = false;
  }
}

function showUpdatePrompt() {
  if (promptShown) return;
  promptShown = true;

  const bar = document.createElement('div');
  bar.setAttribute('role', 'alert');
  bar.style.cssText = [
    'position:fixed', 'left:50%', 'bottom:16px', 'transform:translateX(-50%)',
    'z-index:2147483647',
    'background:#0f4c81', 'color:#fff',
    'font:600 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'padding:12px 16px', 'border-radius:10px',
    'box-shadow:0 10px 30px rgba(0,0,0,0.25)',
    'display:flex', 'align-items:center', 'gap:12px',
    'max-width:calc(100vw - 24px)'
  ].join(';');
  bar.innerHTML =
    '<span>A newer version of the portal is available.</span>' +
    '<button type="button" style="' +
      'background:#fff;color:#0f4c81;border:0;border-radius:6px;' +
      'padding:6px 12px;font-weight:700;cursor:pointer;">Refresh</button>' +
    '<button type="button" aria-label="Dismiss" style="' +
      'background:transparent;color:#fff;border:0;font-size:18px;' +
      'cursor:pointer;padding:0 4px;line-height:1;">&times;</button>';

  const [, refreshBtn, dismissBtn] = bar.querySelectorAll('span, button');
  refreshBtn.addEventListener('click', () => {
    // Force a network roundtrip for the entry HTML.
    window.location.reload(true);
  });
  dismissBtn.addEventListener('click', () => {
    if (bar.parentNode) bar.parentNode.removeChild(bar);
  });

  const attach = () => document.body && document.body.appendChild(bar);
  if (document.body) attach(); else document.addEventListener('DOMContentLoaded', attach, { once: true });
}

export function installVersionCheck(router) {
  if (DISABLED) return;
  // Check once on boot (small delay so the first paint isn't blocked).
  setTimeout(checkNow, 5000);
  // Check on every route change (natural user interaction cadence).
  if (router && typeof router.afterEach === 'function') {
    router.afterEach(() => { checkNow(); });
  }
  // Also when the user returns to the tab.
  window.addEventListener('focus', checkNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkNow();
  });
}
